// A month, typed on the phone's number pad.
//
// A masked field and the phone's own numeric keypad — no calendar.
//
// There is no native month picker on iOS: `<input type="month">` renders as a
// plain text box there, and `type="date"` would demand a day we then throw
// away. A numeric keypad with an MM/YYYY mask is the same gesture on both
// platforms, and it matches the month fields the rest of this application has
// always used.
//
// A month that has not happened yet is never committed.

import { useEffect, useState } from 'react'

import {
  fromMonthYearValue,
  toMonthYearValue,
} from 'src/utils/date/formatMonthPeriod'
import { currentMonthIndex } from 'src/utils/date/monthPeriods'

/** Shown when a driver types a month that has not happened yet. */
export const FUTURE_MONTH_MESSAGE = 'Enter a month in the past.'

/**
 * The earliest month worth accepting. A four-digit year field takes a typo as
 * readily as a date — 06/1199 is a keystroke away from 06/1999 — and no working
 * driver's history starts before this.
 */
export const EARLIEST_YEAR = 1980
export const EARLIEST_MONTH = EARLIEST_YEAR * 12

export const tooEarlyMessage = (minMonth: number): string =>
  minMonth <= EARLIEST_MONTH
    ? `Enter a month after ${EARLIEST_YEAR}.`
    : 'This is before the start date.'

export interface MonthFieldProps {
  label: string
  /** Month index, or null when empty. */
  value: number | null
  onChange: (monthIndex: number | null) => void
  required?: boolean
  /** Error from the entry's own validation, shown under the field. */
  error?: string
  /** Latest selectable month. Defaults to the current one. */
  maxMonth?: number
  /** Earliest selectable month. Raised above 1980 to bound an end by its start. */
  minMonth?: number
  placeholder?: string
  disabled?: boolean
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

/** Digits only, punctuated as the driver types: 032020 -> 03/2020. */
const mask = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 6)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

export const MonthField = ({
  label,
  value,
  onChange,
  required = false,
  error,
  maxMonth,
  minMonth,
  placeholder = 'Month / Year',
  disabled = false,
}: MonthFieldProps) => {
  const latest = maxMonth ?? currentMonthIndex()
  const earliest = Math.max(minMonth ?? EARLIEST_MONTH, EARLIEST_MONTH)
  const [typed, setTyped] = useState(
    value == null ? '' : toMonthYearValue(value)
  )
  const [refused, setRefused] = useState<'future' | 'early' | null>(null)

  // Follow the value when it changes underneath us — a gap fills the end date in
  useEffect(() => {
    setTyped(value == null ? '' : toMonthYearValue(value))
    setRefused(null)
  }, [value])

  const handleTyping = (raw: string) => {
    const next = mask(raw)
    setTyped(next)

    if (next === '') {
      setRefused(null)
      onChange(null)
      return
    }

    const parsed = fromMonthYearValue(next)
    if (parsed == null) {
      // Still half-typed; say nothing until it is a whole month
      setRefused(null)
      return
    }
    if (parsed > latest) {
      setRefused('future')
      return
    }
    if (parsed < earliest) {
      setRefused('early')
      return
    }
    setRefused(null)
    onChange(parsed)
  }

  const shown =
    error ??
    (refused === 'future'
      ? FUTURE_MONTH_MESSAGE
      : refused === 'early'
      ? tooEarlyMessage(earliest)
      : undefined)

  return (
    <div>
      <div className="mb-1 text-[11px] font-medium text-doubleNickel-gray-600">
        {label}
        {required && (
          <span className="ml-0.5 text-doubleNickel-error-600">*</span>
        )}
      </div>
      <div
        className={join(
          'flex items-center justify-between gap-2',
          shown
            ? 'border-b-2 border-doubleNickel-error-600'
            : 'border-b border-doubleNickel-gray-300'
        )}
      >
        <input
          type="text"
          // The phone's number pad, which is the native part that matters here
          inputMode="numeric"
          pattern="[0-9/]*"
          autoComplete="off"
          maxLength={7}
          aria-label={label}
          aria-invalid={shown ? true : undefined}
          disabled={disabled}
          value={typed}
          placeholder={placeholder}
          onChange={(event) => handleTyping(event.target.value)}
          className="w-full bg-transparent py-1.5 text-[15px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400"
        />
      </div>
      {shown && (
        <div className="mt-0.5 text-[10px] text-doubleNickel-error-700">
          {shown}
        </div>
      )}
    </div>
  )
}
